/// <amd-module name="VSSUI/Components/Table/ColumnSelect" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { Checkbox, TriStateCheckbox } from "VSSUI/Checkbox";
import { IListRow } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import * as Resources from "VSSUI/Resources.Widgets";
import { css, KeyCode, preventDefault } from "VSSUI/Util";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { ISelection } from "VSSUI/Utilities/Selection";
import { IMeasurementStyle, ITableColumn, ITableColumnBehaviorProps, TableColumnLayout } from "./Table.Props";

interface IColumnSelectProps {
    excludeFocusZone?: boolean;
    role?: string;
}

/**
 * ColumnSelect is used to render additional selection ui for a given row.
 *
 * This renders a fixed width column that contains a checkbox in the header
 * as well as each row that represents the selection state of the row. It
 * also allows the user to change the selection state. The header checkbox
 * is used to set the state of all rows in the table to the same state.
 *
 */
export class ColumnSelect implements ITableColumn<{}> {
    private itemProvider?: IItemProvider<{}>;
    private ariaHidden: boolean;
    private selection?: ISelection;
    private onSelect?: (event: React.SyntheticEvent<HTMLElement, Event>, tableRow: IListRow<{}>) => void;
    private props: IColumnSelectProps | undefined;
    private isListbox: boolean = false;

    private columnSelectBehavior: IBehavior<ITableColumnBehaviorProps<{}>, {}> = {
        initialize: (props: ITableColumnBehaviorProps<{}>) => {
            this.ariaHidden = props.tableProps.role === "menu";
            this.itemProvider = props.tableProps.itemProvider;
            this.selection = props.tableProps.selection;
            this.onSelect = props.tableProps.onSelect;
            this.isListbox = props.tableProps.role === "listbox";
        },

        componentDidMount: (props: ITableColumnBehaviorProps<{}>) => {
            this.componentDidMount(props);
        },

        componentDidUpdate: (props: ITableColumnBehaviorProps<{}>) => {
            this.componentDidUpdate(props);
        },

        componentWillUnmount: () => {
            this.componentWillUnmount();
        }
    };

    private allSelected: ObservableValue<boolean | undefined> = new ObservableValue<boolean | undefined>(false);
    public columnLayout = TableColumnLayout.none;
    public id = "_select";
    public width = 2.5;
    public widthStyle = IMeasurementStyle.REM;

    public behaviors = [this.columnSelectBehavior];

    public constructor(props?: IColumnSelectProps) {
        this.props = props;
    }

    public componentDidMount(props: ITableColumnBehaviorProps<{}>) {
        const { itemProvider, selection } = this;
        // We need to know about changes to the selection to manage the selectAll state.
        if (selection) {
            selection.subscribe(this.onSelectionChange);
            this.onSelectionChange();
        }
        if (itemProvider && itemProvider.subscribe) {
            itemProvider.subscribe(this.onSelectionChange);
        }
    }

    public componentDidUpdate(props: ITableColumnBehaviorProps<{}>) {
        let { selection } = this;
        if (selection !== props.tableProps.selection) {
            if (selection) {
                selection.unsubscribe(this.onSelectionChange);
            }
            selection = props.tableProps.selection;
            this.selection = selection;
            if (selection) {
                selection.subscribe(this.onSelectionChange);
            }
        }
        if (selection) {
            this.onSelectionChange();
        }
        if (this.itemProvider !== props.tableProps.itemProvider) {
            if (this.itemProvider && this.itemProvider.unsubscribe) {
                this.itemProvider.unsubscribe(this.onSelectionChange);
            }
            this.itemProvider = props.tableProps.itemProvider;
            if (this.itemProvider && this.itemProvider.subscribe) {
                this.itemProvider.subscribe(this.onSelectionChange);
            }
        }
    }

    public componentWillUnmount() {
        const { selection } = this;
        if (selection) {
            selection.unsubscribe(this.onSelectionChange);
        }
        if (this.itemProvider && this.itemProvider.unsubscribe) {
            this.itemProvider.unsubscribe(this.onSelectionChange);
        }
    }

    public renderCell = (rowIndex: number, columnIndex: number, listColumn: ITableColumn<{}>): JSX.Element => {
        const { selection } = this;
        const selected = selection && selection.selected(rowIndex);
        const selectable = selection && selection.selectable(rowIndex);

        return (
            document.body.classList.contains('dropdown-list-component-enabled') && this.isListbox ? (
                <div
                    aria-hidden={this.ariaHidden}
                    className={css("bolt-table-cell-select bolt-table-cell bolt-list-cell flex-noshrink", "col-" + columnIndex)}
                    data-column-index={columnIndex}
                    key="col-select"
                    onClick={preventDefault}
                    onDoubleClick={preventDefault}
                    onMouseDown={event => {
                        this.updateSelection(event, rowIndex);
                        event.preventDefault();
                    }}
                    onKeyDown={event => {
                        if (event.which === KeyCode.space) {
                            this.updateSelection(event, rowIndex);
                            event.preventDefault();
                        }
                    }}
                >
                    <div className="flex-row justify-center">
                        <span className="flex-row-inline">
                            <Checkbox
                                ariaLabel={this.props?.role === "presentation" ? undefined : Resources.SelectRowLabel}
                                checked={!!selected}
                                excludeFocusZone={this.props?.excludeFocusZone}
                                excludeTabStop={true}
                                disabled={!selectable}
                            />
                        </span>
                    </div>
                </div>
            ) : (
                <td
                    aria-colindex={columnIndex + 1}
                    aria-hidden={this.ariaHidden}
                    className={css("bolt-table-cell-select bolt-table-cell bolt-list-cell", "col-" + columnIndex)}
                    data-column-index={columnIndex}
                    key="col-select"
                    onClick={preventDefault}
                    onDoubleClick={preventDefault}
                    onMouseDown={event => {
                        this.updateSelection(event, rowIndex);
                        event.preventDefault();
                    }}
                    onKeyDown={event => {
                        if (event.which === KeyCode.space) {
                            this.updateSelection(event, rowIndex);
                            event.preventDefault();
                        }
                    }}
                >
                    <div className="flex-row justify-center">
                        <span className="flex-row-inline">
                            <Checkbox
                                ariaLabel={this.props?.role === "presentation" ? undefined : Resources.SelectRowLabel}
                                checked={!!selected}
                                excludeFocusZone={this.props?.excludeFocusZone}
                                excludeTabStop={true}
                                disabled={!selectable}
                            />
                        </span>
                    </div>
                </td>
            )

        );
    };

    public renderHeaderCell = (columnIndex: number, listColumn: ITableColumn<{}>, focuszoneId?: string): JSX.Element => {
        return (
            <th
                aria-colindex={columnIndex + 1}
                className={css("bolt-table-cell-select bolt-table-header-cell", "col-header-" + columnIndex)}
                data-column-index={columnIndex}
                key="col-select"
            >
                <div className="flex-row">
                    <Observer allSelected={this.allSelected}>
                        {(props: { allSelected: boolean | undefined }) => {
                            const { itemProvider, selection } = this;

                            // Get the total number of items within the list.
                            const itemCount = itemProvider && itemProvider.length;

                            return selection && selection.multiSelect && itemCount !== -1 ? (
                                <div className="flex-row flex-grow justify-center">
                                    <TriStateCheckbox
                                        ariaLabel={listColumn.ariaLabel ?? Resources.SelectAllRowsLabel}
                                        checked={props.allSelected}
                                        focuszoneId={focuszoneId}
                                        onChange={this.onChangeHeader}
                                    />
                                </div>
                            ) : (
                                <div className="visually-hidden">
                                    {listColumn.ariaLabel ?? Resources.SelectionColumnLabel}
                                </div>
                            );
                        }}
                    </Observer>
                </div>
            </th>
        );
    };

    private onChangeHeader = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const { itemProvider, onSelect, selection } = this;

        // toggle select all
        if (selection) {
            if (this.allSelected.value !== false) {
                selection.clear();
                announce(Resources.AllRowsUnselectedMessage, true);
            } else {
                selection.select(0, itemProvider && itemProvider.length);
                announce(Resources.AllRowsSelectedMessage, true);
            }
        }

        if (onSelect && itemProvider) {
            for (let i = 0; i < itemProvider.length; i++) {
                onSelect(event, this.getListRow(i));
            }
        }
    };

    private onSelectionChange = () => {
        const { itemProvider, selection } = this;

        if (selection) {
            const selectedCount = selection.selectedCount;
            const itemCount = itemProvider && itemProvider.length - selection.unselectableCount;

            if (selectedCount > 0) {
                if (selectedCount === itemCount) {
                    this.allSelected.value = true;
                } else {
                    this.allSelected.value = undefined;
                }
            } else {
                this.allSelected.value = false;
            }
        }
    };

    private updateSelection = (event: React.SyntheticEvent<HTMLElement, Event>, rowIndex: number) => {
        const { onSelect, selection } = this;

        if (selection) {
            if (selection.selected(rowIndex)) {
                selection.unselect(rowIndex);
            } else {
                selection.select(rowIndex, 1, true);
            }
        }
        if (onSelect) {
            const listRow = this.getListRow(rowIndex);
            onSelect(event, listRow);
        }
    };

    private getListRow = (rowIndex: number): IListRow<{}> => {
        return { data: this.itemProvider ? this.itemProvider.value[rowIndex] : {}, index: rowIndex };
    };
}
